import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Send, AlertTriangle, Phone, Mail, MessageCircle, Building2,
  User, MapPin, Home, BadgeIndianRupee,
} from "lucide-react";
import { api, fmtDateTime, fmtINR } from "@/lib/api";
import { Skeleton, StatusPill, PriorityDot, Pill } from "@/components/Atoms";

const fetcher = (u) => api.get(u).then((r) => r.data);

export default function CRMTicketDetail() {
  const { id } = useParams();
  const key = `/tickets/${id}`;
  const { data } = useSWR(key, fetcher, { refreshInterval: 10000 });
  const { data: employees } = useSWR("/employees", fetcher);
  const { data: customer360 } = useSWR(data?.context?.customer?.customer_id ? `/customers/${data.context.customer.customer_id}` : null, fetcher);
  const [msg, setMsg] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!data) return <Skeleton h={400}/>;

  const ctx = data.context || {};
  const customer = ctx.customer;
  const project = ctx.project;
  const subproject = ctx.subproject;
  const flat = ctx.flat;
  const floor = ctx.floor;

  const send = async () => {
    if (!msg.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${id}/messages`, { message: msg, is_internal: internal });
      setMsg("");
      mutate(key);
    } finally { setBusy(false); }
  };

  const patch = async (body) => {
    try {
      await api.patch(`/tickets/${id}`, body);
      mutate(key);
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-4">
      <Link to="/crm/tickets" className="rc-btn rc-btn-sm rc-btn-ghost -ml-3"><ChevronLeft size={16}/> Tickets</Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        <div className="space-y-4">
          {/* Property + Customer context strip */}
          {(project || customer) && (
            <div className="rc-card rc-card-padded" data-testid="ticket-context-card">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="rc-eyebrow">Property &amp; Buyer</div>
                {customer && (
                  <Link to={`/crm/customers/${customer.customer_id}`} className="rc-btn rc-btn-sm rc-btn-ghost" data-testid="open-customer-360">
                    Open Customer 360 <ChevronLeft size={14} className="rotate-180"/>
                  </Link>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Property */}
                <div>
                  <div className="rc-caption uppercase tracking-wider mb-2" style={{ fontSize: 10 }}>Property</div>
                  <div className="flex items-start gap-3">
                    <div className="rc-icon-tile rc-icon-tile-blue flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 10 }}>
                      <Building2 size={18}/>
                    </div>
                    <div className="min-w-0">
                      <div className="rc-headline truncate" data-testid="ctx-project">{project?.project_name || "—"}</div>
                      <div className="rc-footnote flex items-center gap-1 flex-wrap">
                        {project?.location && <span className="flex items-center gap-1"><MapPin size={11}/>{project.location}</span>}
                      </div>
                      <div className="rc-footnote mt-1 flex items-center gap-1 flex-wrap">
                        <span className="rc-pill rc-pill-blue" style={{ height: 20, fontSize: 12 }}>
                          {subproject?.subproject_name || "—"}
                        </span>
                        {flat?.flat_number && (
                          <span className="rc-pill rc-pill-grey" style={{ height: 20, fontSize: 12 }}>
                            <Home size={11} className="mr-1"/>Flat {flat.flat_number}
                          </span>
                        )}
                        {floor?.floor_number != null && (
                          <span className="rc-pill rc-pill-grey" style={{ height: 20, fontSize: 12 }}>
                            Floor {floor.floor_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Customer */}
                <div>
                  <div className="rc-caption uppercase tracking-wider mb-2" style={{ fontSize: 10 }}>Buyer</div>
                  <div className="flex items-start gap-3">
                    <div className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0" style={{ background: "linear-gradient(135deg, #0F4B7F 0%, #1976D2 100%)" }}>
                      {(customer?.customer_name || "").split(" ").map(s => s[0]).slice(0, 2).join("") || <User size={16}/>}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="rc-headline truncate" data-testid="ctx-customer-name">{customer?.customer_name || "Unknown buyer"}</div>
                      <div className="rc-footnote truncate">{customer?.email || "—"}</div>
                      <div className="rc-footnote rc-tabular">{customer?.primary_phone || "—"}</div>
                    </div>
                  </div>
                  {customer?.primary_phone && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <a href={`tel:${customer.primary_phone}`} className="rc-btn rc-btn-sm rc-btn-secondary"><Phone size={14}/>Call</a>
                      <a href={`mailto:${customer.email}`} className="rc-btn rc-btn-sm rc-btn-secondary"><Mail size={14}/>Email</a>
                      <a href={`https://wa.me/91${customer.primary_phone}`} target="_blank" rel="noreferrer" className="rc-btn rc-btn-sm rc-btn-secondary"><MessageCircle size={14}/>WhatsApp</a>
                    </div>
                  )}
                </div>
              </div>

              {customer360?.financials?.summary && (
                <div className="mt-4 pt-4 border-t border-[var(--rc-line)] grid grid-cols-3 gap-3">
                  <div>
                    <div className="rc-caption uppercase" style={{ fontSize: 10 }}>Demand</div>
                    <div className="rc-headline rc-tabular">{fmtINR(customer360.financials.summary.total_demand)}</div>
                  </div>
                  <div>
                    <div className="rc-caption uppercase" style={{ fontSize: 10 }}>Paid</div>
                    <div className="rc-headline rc-tabular" style={{ color: "var(--rc-success)" }}>{fmtINR(customer360.financials.summary.total_paid)}</div>
                  </div>
                  <div>
                    <div className="rc-caption uppercase" style={{ fontSize: 10 }}>Outstanding</div>
                    <div className="rc-headline rc-tabular" style={{ color: customer360.financials.summary.outstanding > 0 ? "var(--rc-orange)" : "var(--rc-success)" }}>{fmtINR(customer360.financials.summary.outstanding)}</div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="rc-card rc-card-padded">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><PriorityDot priority={data.ticket.priority}/>
                  <div className="rc-title-2 break-words">{data.ticket.subject}</div></div>
                <div className="rc-footnote mt-1">{data.ticket.category} · #{data.ticket.ticket_id.slice(0,8)}</div>
              </div>
              <StatusPill status={data.ticket.sla_breached ? "breached" : data.ticket.status} />
            </div>
            <div className="rc-body mt-3 whitespace-pre-wrap">{data.ticket.description}</div>
          </div>

          <div className="rc-card rc-card-padded">
            <div className="rc-title-3 mb-3">Conversation</div>
            <div className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
              {data.updates.map((u) => (
                <div key={u.update_id} className={`flex ${u.updated_by_type === "buyer" ? "justify-end" : "justify-start"}`}>
                  <div>
                    <div className={`rc-bubble ${u.is_internal ? "rc-bubble-internal" : u.updated_by_type === "buyer" ? "rc-bubble-customer" : "rc-bubble-team"}`}>
                      {u.is_internal && <div className="rc-caption mb-1">Internal note</div>}
                      {u.update_text}
                    </div>
                    <div className="rc-caption mt-1 text-center">{u.updated_by_name || u.updated_by_type} · {fmtDateTime(u.created_at)}</div>
                  </div>
                </div>
              ))}
              {data.updates.length === 0 && <div className="rc-footnote text-center py-6">No messages yet.</div>}
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <div className="flex gap-2 items-center">
                <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} className="rc-input flex-1" placeholder={internal ? "Internal note (not visible to buyer)..." : "Type a reply..."} data-testid="crm-reply-input"/>
                <button onClick={send} disabled={busy || !msg.trim()} className="rc-btn rc-btn-primary rc-btn-sm" data-testid="crm-reply-send"><Send size={16}/></button>
              </div>
              <label className="rc-footnote flex items-center gap-2"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="internal-toggle"/> Internal note</label>
            </div>
          </div>
        </div>

        <aside className="space-y-3">
          <div className="rc-card rc-card-padded">
            <div className="rc-caption uppercase tracking-wider mb-3">SLA</div>
            <div className="rc-headline rc-tabular">{fmtDateTime(data.ticket.sla_deadline)}</div>
            <div className="rc-footnote">Created {fmtDateTime(data.ticket.created_at)}</div>
          </div>

          <div className="rc-card rc-card-padded space-y-3">
            <div>
              <div className="rc-caption uppercase tracking-wider mb-1">Status</div>
              <select className="rc-input !h-10" defaultValue={data.ticket.status} onChange={(e) => patch({ status: e.target.value })} data-testid="status-select">
                <option value="open">Open</option><option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option><option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <div className="rc-caption uppercase tracking-wider mb-1">Priority</div>
              <select className="rc-input !h-10" defaultValue={data.ticket.priority} onChange={(e) => patch({ priority: e.target.value })} data-testid="priority-select">
                <option>P1</option><option>P2</option><option>P3</option><option>P4</option>
              </select>
            </div>
            <div>
              <div className="rc-caption uppercase tracking-wider mb-1">Assigned to</div>
              <select className="rc-input !h-10" defaultValue={data.ticket.assigned_to || ""} onChange={(e) => patch({ assigned_to: parseInt(e.target.value) })} data-testid="assignee-select">
                <option value="">Unassigned</option>
                {employees?.filter(e=>e.role_key && ["crm_executive","crm_manager"].includes(e.role_key)).map(e => <option key={e.employee_id} value={e.employee_id}>{e.employee_name}</option>)}
              </select>
            </div>
            <button onClick={() => patch({ priority: "P1" })} className="rc-btn rc-btn-sm rc-btn-destructive w-full" data-testid="escalate-btn">
              <AlertTriangle size={14}/> Escalate to P1
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
